import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../app/useAuth'
import { Button, Dialog, EmptyState, Field, Input, PageHeader, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { Picture } from '../../components/Picture'
import { toast } from '../../components/toast'
import { ApiError, api, type OnboardedSchool, type PlatformSchool, type SandboxSchool } from '../../lib/api/client'
import { invitationUrl } from '../../lib/invitationUrl'
import './schools.css'

type PersonForm = { full_name: string; email: string }
type SchoolForm = {
  name: string
  branch: string
  school_admin: PersonForm
}

const EMPTY_FORM: SchoolForm = {
  name: '',
  branch: '',
  school_admin: { full_name: '', email: '' },
}

export function Schools() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const schools = useQuery({
    queryKey: ['platform', 'schools'],
    queryFn: api.platformSchools,
  })
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<SchoolForm>(EMPTY_FORM)
  const [logo, setLogo] = useState<File | null>(null)
  const [logoWarning, setLogoWarning] = useState('')
  const [created, setCreated] = useState<OnboardedSchool | null>(null)
  const [sandbox, setSandbox] = useState<SandboxSchool | null>(null)
  const [removing, setRemoving] = useState<PlatformSchool | null>(null)

  const makeSandbox = useMutation({
    mutationFn: () => api.createSandbox(),
    onSuccess: async (result) => {
      setSandbox(result)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
    },
    onError: (error) =>
      toast(
        error instanceof ApiError ? error.message : 'The sandbox could not be created.',
        'error',
      ),
  })

  const removeSandbox = useMutation({
    mutationFn: (schoolId: string) => api.deleteSchool(schoolId),
    onSuccess: async (summary) => {
      setRemoving(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
      toast(
        `${summary.school_name} is gone, with its ${summary.papers} papers and ${summary.people} accounts.`,
      )
    },
    onError: (error) =>
      toast(
        error instanceof ApiError ? error.message : 'That school could not be deleted.',
        'error',
      ),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.setSchoolActive(id, active),
    onSuccess: async (school) => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
      toast(
        school.is_active
          ? `${school.name} is back on. Everyone can sign in again.`
          : `${school.name} is paused. Nothing has been deleted.`,
      )
    },
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'That did not work.', 'error'),
  })

  const create = useMutation({
    mutationFn: async () => {
      const result = await api.onboardSchool(form)
      if (!logo) return { result, logoWarning: '' }
      try {
        const school = await api.uploadSchoolLogo(logo, result.school.id)
        return {
          result: {
            ...result,
            school: { ...result.school, logo_image_id: school.logo_image_id },
          },
          logoWarning: '',
        }
      } catch (error) {
        return {
          result,
          logoWarning:
            error instanceof ApiError
              ? error.message
              : 'The school was created, but its logo could not be uploaded.',
        }
      }
    },
    onSuccess: async ({ result, logoWarning: warning }) => {
      setCreated(result)
      setLogoWarning(warning)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
      await queryClient.invalidateQueries({ queryKey: ['templates', result.school.id] })
      toast(
        warning
          ? `${result.school.name} was created, but its logo needs attention.`
          : `${result.school.name} is ready. Share the administrator invitation link.`,
        warning ? 'error' : undefined,
      )
    },
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'The school could not be created.', 'error'),
  })

  const close = () => {
    setOpen(false)
    setCreated(null)
    setForm(EMPTY_FORM)
    setLogo(null)
    setLogoWarning('')
    create.reset()
  }

  const setAdministrator = (field: keyof PersonForm, value: string) =>
    setForm((current) => ({
      ...current,
      school_admin: { ...current.school_admin, [field]: value },
    }))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate()
  }

  if (schools.isLoading) return <Spinner label="Loading schools" />

  // The site administrator belongs to an internal platform workspace so the
  // normal account and branding machinery can be reused. It is not an onboarded
  // school and should not create a false role-gap row in this directory.
  const schoolRows = (schools.data ?? []).filter((school) => school.id !== user?.school_id)

  return (
    <div className="tc-page tc-schools">
      <PageHeader
        eyebrow="Platform administration"
        title="Schools"
        icon={<Icon.schools size={20} />}
        description="Onboard schools and hand day-to-day people management to each school administrator."
        actions={
          <>
            {/* Reproduce a reported problem here rather than in the school that
                reported it - their unpublished papers are theirs. */}
            <Button
              disabled={makeSandbox.isPending}
              onClick={() => makeSandbox.mutate()}
              title="A throwaway school with staff accounts and papers at every stage, for reproducing a reported problem"
            >
              {makeSandbox.isPending ? 'Building…' : 'New sandbox school'}
            </Button>
            <Button
              variant="primary"
              icon={<Icon.plus size={16} />}
              onClick={() => setOpen(true)}
            >
              Onboard school
            </Button>
          </>
        }
      />

      <PlatformOverview schools={schoolRows} />

      <section className="tc-school-directory" aria-labelledby="school-directory-title">
        <div className="tc-section-heading">
          <div>
            <h2 id="school-directory-title">School directory</h2>
            <p>
              {schoolRows.length
                ? `${schoolRows.length} ${schoolRows.length === 1 ? 'school' : 'schools'} onboarded`
                : 'Schools will appear here after onboarding'}
            </p>
          </div>
          <Pill tone="secondary">{schoolRows.length} total</Pill>
        </div>

        {!schoolRows.length ? (
          <EmptyState
            title="No schools yet"
            message="Onboard the first school with its administrator. They can invite the rest of the team."
            action={<Button variant="primary" onClick={() => setOpen(true)}>Onboard school</Button>}
          />
        ) : (
          <div className="tc-school-table-wrap">
            <table className="tc-school-table">
              <thead>
                <tr>
                  <th>School and branch</th>
                  <th>School team</th>
                  <th>People</th>
                  <th>Handoff</th>
                  <th><span className="tc-sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {schoolRows.map((school) => (
                  <SchoolRow
                    school={school}
                    key={school.id}
                    busy={setActive.isPending || removeSandbox.isPending}
                    onDelete={() => setRemoving(school)}
                    onToggleActive={() =>
                      setActive.mutate({ id: school.id, active: !school.is_active })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog
        open={open}
        title={created ? `${created.school.name} is ready` : 'Onboard a school'}
        onClose={close}
        footer={
          created ? (
            <Button variant="primary" onClick={close}>Done</Button>
          ) : (
            <>
              <Button onClick={close}>Cancel</Button>
              <Button
                variant="primary"
                form="tc-onboard-school"
                type="submit"
                disabled={create.isPending}
              >
                {create.isPending ? 'Creating…' : 'Create school'}
              </Button>
            </>
          )
        }
      >
        {created ? (
          <>
            {logoWarning && (
              <p className="tc-onboard-warning" role="alert">
                <Icon.warning size={18} />
                <span>{logoWarning} You can add it later from School details.</span>
              </p>
            )}
            <InvitationLinks result={created} />
          </>
        ) : (
          <form id="tc-onboard-school" className="tc-onboard-form" onSubmit={submit}>
            <div className="tc-form-grid">
              <Field label="School name">
                <Input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </Field>
              <Field label="Branch" hint="For example: North Campus">
                <Input value={form.branch} onChange={(event) => setForm({ ...form, branch: event.target.value })} />
              </Field>
            </div>
            <Field
              label="School logo"
              hint="Shown beside the school name across the website and used by the default paper header."
            >
              <label className="tc-onboard-logo">
                <span className="tc-onboard-logo-preview" aria-hidden="true">
                  <Icon.pictures size={22} />
                </span>
                <span>
                  <strong>{logo?.name ?? 'Choose a logo'}</strong>
                  <small>PNG, JPEG or WebP</small>
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => setLogo(event.target.files?.[0] ?? null)}
                />
              </label>
            </Field>
            <fieldset>
              <legend>School administrator</legend>
              <p className="tc-onboard-admin-note">
                This person will invite teachers, reviewers and printing staff from People &amp; roles.
              </p>
              <div className="tc-form-grid">
                <Field label="Full name">
                  <Input required value={form.school_admin.full_name} onChange={(event) => setAdministrator('full_name', event.target.value)} />
                </Field>
                <Field label="Email">
                  <Input required type="email" value={form.school_admin.email} onChange={(event) => setAdministrator('email', event.target.value)} />
                </Field>
              </div>
            </fieldset>
          </form>
        )}
      </Dialog>

      {sandbox && (
        <Dialog
          open
          title={`${sandbox.name} is ready to test in`}
          onClose={() => setSandbox(null)}
          footer={<Button variant="primary" onClick={() => setSandbox(null)}>Done</Button>}
        >
          <p>
            Four sign-ins, and papers already sitting at every stage of the workflow.
            Sign in as whichever role the report is about.
          </p>
          <p className="tc-sandbox-warning">
            <Icon.warning size={16} />
            <span>
              These passwords are shown once and are not stored anywhere. Copy what you
              need before closing - a sandbox you cannot sign in to is only good for
              deleting.
            </span>
          </p>
          <table className="tc-sandbox-accounts">
            <thead>
              <tr>
                <th>Role</th>
                <th>Email</th>
                <th>Password</th>
              </tr>
            </thead>
            <tbody>
              {sandbox.accounts.map((account) => (
                <tr key={account.email}>
                  <td>{account.roles.map(roleLabel).join(', ')}</td>
                  <td><code>{account.email}</code></td>
                  <td><code>{account.password}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Dialog>
      )}

      {removing && (
        <Dialog
          open
          title={`Delete ${removing.name}?`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <Button onClick={() => setRemoving(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={removeSandbox.isPending}
                onClick={() => removeSandbox.mutate(removing.id)}
              >
                {removeSandbox.isPending ? 'Deleting…' : 'Delete it and everything in it'}
              </Button>
            </>
          }
        >
          <p>
            Its accounts, papers and pictures go with it, and none of it can be brought
            back. Pictures other schools also hold are left alone.
          </p>
          <p className="tc-field-hint">
            Only sandbox schools can be deleted. A real school is paused instead, which
            keeps every paper.
          </p>
        </Dialog>
      )}
    </div>
  )
}

function PlatformOverview({ schools }: { schools: PlatformSchool[] }) {
  const ready = schools.filter((school) => school.administrator_count > 0).length
  const active = schools.reduce((total, school) => total + school.active_users, 0)
  const pending = schools.reduce((total, school) => total + school.pending_invitations, 0)
  const stats = [
    { label: 'Schools', value: schools.length, note: 'Onboarded', tone: 'violet' },
    { label: 'Handed off', value: ready, note: 'Administrator assigned', tone: 'success' },
    { label: 'Active people', value: active, note: 'Accepted accounts', tone: 'neutral' },
    {
      label: 'Pending invites',
      value: pending,
      note: pending ? 'Awaiting acceptance' : 'Everyone has joined',
      tone: pending ? 'warning' : 'success',
    },
  ]

  return (
    <section className="tc-platform-overview" aria-label="Platform overview">
      {stats.map((stat) => (
        <div className={`tc-platform-stat is-${stat.tone}`} key={stat.label}>
          <span>{stat.label}</span>
          <strong>{stat.value}</strong>
          <small>{stat.note}</small>
        </div>
      ))}
    </section>
  )
}

function SchoolRow({
  school,
  busy,
  onDelete,
  onToggleActive,
}: {
  school: PlatformSchool
  busy: boolean
  onDelete: () => void
  onToggleActive: () => void
}) {
  const ready = school.administrator_count > 0

  return (
    <tr>
      <td>
        <div className="tc-school-identity">
          <span className="tc-school-monogram" aria-hidden="true">
            {school.logo_image_id ? (
              <Picture id={school.logo_image_id} className="tc-school-monogram-image" />
            ) : (
              school.name.slice(0, 2).toUpperCase()
            )}
          </span>
          <div>
            <strong>{school.name}</strong>
            <span className="tc-school-branch">
              {school.branch || 'Main branch'}
              {school.is_sandbox && <Pill tone="secondary">Sandbox</Pill>}
              {!school.is_active && <Pill tone="warning">Paused</Pill>}
            </span>
            <Link className="tc-school-manage-link" to={`/schools/${school.id}/settings`}>
              View and manage school
            </Link>
          </div>
        </div>
      </td>
      <td>
        <div className="tc-role-coverage">
          <RoleCount label="Admin" count={school.administrator_count} required />
          <RoleCount label="Reviewer" count={school.reviewer_count} />
          <RoleCount label="Teacher" count={school.teacher_count} />
        </div>
      </td>
      <td>
        <div className="tc-people-summary">
          <span><strong>{school.active_users}</strong> active</span>
          <span><strong>{school.pending_invitations}</strong> pending</span>
        </div>
      </td>
      <td>
        <div className="tc-readiness">
          <Pill tone={ready ? 'success' : 'warning'}>{ready ? 'Handed off' : 'Needs admin'}</Pill>
          <span>{ready ? 'Administrator manages the team' : 'Assign a school administrator'}</span>
        </div>
      </td>
      <td>
        {/* A sandbox can be destroyed because it is ours. A real school can only
            be paused - offboarding must never be a step away from deleting a
            term's exam papers. */}
        <div className="tc-school-actions">
          {school.is_sandbox ? (
            <Button size="sm" variant="danger" disabled={busy} onClick={onDelete}>
              Delete
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={busy}
              onClick={onToggleActive}
              title={
                school.is_active
                  ? 'Pause access for everyone at this school. Nothing is deleted.'
                  : 'Let this school sign in again.'
              }
            >
              {school.is_active ? 'Pause' : 'Restore'}
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function RoleCount({ label, count, required = false }: { label: string; count: number; required?: boolean }) {
  return (
    <span className={`tc-role-count ${required && !count ? 'is-missing' : ''}`}>
      <strong>{count}</strong>
      <small>{label}</small>
    </span>
  )
}

function InvitationLinks({ result }: { result: OnboardedSchool }) {
  return (
    <div className="tc-invite-links">
      <p>
        Share this secure link with the school administrator. After joining, they can build
        the team in People &amp; roles.
      </p>
      {result.invitations.map((invite) => {
        const link = invitationUrl(invite.token)
        return (
          <div className="tc-invite-link" key={invite.id}>
            <div>
              <strong>{invite.full_name}</strong>
              <span>{invite.roles.map(roleLabel).join(', ')} · {invite.email}</span>
            </div>
            <Input readOnly value={link} aria-label={`Invitation link for ${invite.full_name}`} />
            <Button
              icon={<Icon.copy size={15} />}
              onClick={async () => {
                await navigator.clipboard.writeText(link)
                toast(`Copied ${invite.full_name}'s invitation link.`)
              }}
            >
              Copy link
            </Button>
          </div>
        )
      })}
    </div>
  )
}

function roleLabel(role: string) {
  // Named rather than derived: capitalising the raw value turned print_admin
  // into "Print_admin" on screen the moment a second place started using this.
  const named: Record<string, string> = {
    school_admin: 'School administrator',
    print_admin: 'Printing',
    teacher: 'Teacher',
    reviewer: 'Reviewer',
    admin: 'Site administrator',
  }
  return named[role] ?? role.charAt(0).toUpperCase() + role.slice(1)
}
